import {
  lazy,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ChangeEvent } from 'react';
import { useSession } from '../session/context';
import { QcContext } from '../qc/context';
import { useStudioVideo } from '../studio/studioVideoContext';
import { useNav } from '../components/navContext';
import { EmptyState } from '../components/EmptyState';
import { CueTable } from '../editor/CueTable';
import { EditorToolbar } from '../editor/EditorToolbar';
import { QcControls } from '../qc/QcControls';
import { QcThresholds } from '../qc/QcThresholds';
import { StudioExport } from '../editor/StudioExport';
import { WarningList } from '../convert/LossMatrix';
import { TimingControls } from '../sync/TimingControls';
import {
  cueMatches,
  deleteCue,
  findReplace,
  insertCueAfter,
  mergeCues,
  splitCue,
  type FindReplaceOptions,
} from '../editor/ops';
import { indexOfUid, uidAt } from '../editor/uid';
import { useLicense } from '../license/context';
import { UpgradePrompt } from '../license/UpgradePrompt';
import { useCustomPresets } from '../qc/customPresetsContext';
import { resolveRuleset } from '../qc/customPresets';
import { advancedAvailable } from '../editor/formatMarks';
import { TranscribeButton } from '../transcribe/TranscribeButton';
import { track } from '../analytics/client';
import { type Cue } from '../../core';
import { inTextField } from '../keyboard';
import './Editor.css';

const StudioPanel = lazy(() =>
  import('../studio/StudioPanel').then((m) => ({ default: m.StudioPanel })),
);

// The Advanced edit dialog (cue-forge-b0w) is lazy so its editor/normalizer stays
// off the eager bundle — it's opened only on demand, per cue.
const AdvancedEditDialog = lazy(() =>
  import('../editor/AdvancedEditDialog').then((m) => ({ default: m.AdvancedEditDialog })),
);

/**
 * Editor mode (spec §4.1 #3): the virtualized, inline-editable cue table plus a
 * toolbar for find/replace and undo/redo, and per-row merge/split. All edits go
 * through the session's history-aware setDoc, so every change is undoable —
 * including via Ctrl/Cmd+Z and Ctrl+Shift+Z / Ctrl+Y.
 */
export function Editor() {
  const { loaded, setDoc, undo, redo, canUndo, canRedo, dirty } = useSession();
  const { isPro } = useLicense();
  // Video state lives in a shared provider so it survives tab switches and can be
  // restored after a reload. Outside a provider (some tests) fall back to local
  // state — mirrors the optional QC/Nav contexts.
  const studio = useStudioVideo();
  const [localVideo, setLocalVideo] = useState<File | null>(null);
  // The cue currently open in the Advanced edit dialog (cue-forge-b0w), or null.
  const [advCue, setAdvCue] = useState<Cue | null>(null);
  // The per-row Advanced button that launched the dialog, refocused on close so a
  // keyboard-first editor keeps their grid position (cue-forge-drt.12). The other
  // modals' fixed-trigger refs don't fit a per-row launcher, so capture the
  // focused element at launch instead.
  const advLauncherRef = useRef<HTMLElement | null>(null);
  // Two decoupled pointers into doc.cues: the user-chosen cue and the cue under
  // the playhead. Both are stored by stable cue uid (cue-forge-9ww.7), not array
  // position, so a structural edit (merge/split/insert/delete renumbers indices)
  // moves them with their cue instead of silently drifting to a neighbour; they
  // resolve back to row positions at render and drop when the cue is gone. The
  // callbacks receive a position from the table/studio and store its cue's uid,
  // reading the live cue list through a ref since they predate the loaded guard.
  const [selectedUid, setSelectedUid] = useState<string | null>(null);
  const [activeUid, setActiveUid] = useState<string | null>(null);
  // The Timing panel (shift/retime, Pro frame-rate/drift/auto-resync) is a
  // collapsible tool inside Studio (cue-forge-gfw), not a separate mode; closed
  // by default so it doesn't crowd the cue table. Works with no video loaded.
  const [timingOpen, setTimingOpen] = useState(false);
  // Find-as-filter (cue-forge-q51): the toolbar's Find text narrows the cue table
  // to matching cues (the studio/timeline keeps the full doc).
  const [filter, setFilter] = useState<{ find: string; opts: FindReplaceOptions }>({
    find: '',
    opts: {},
  });
  const onFilterChange = useCallback(
    (find: string, opts: FindReplaceOptions) => setFilter({ find, opts }),
    [],
  );
  // Selection is stored by stable cue uid (cue-forge-9ww.7). The cue table works
  // in the (possibly filtered) visible-list space while the studio timeline works
  // in full-doc space, so map each through its own ref.
  const docCuesRef = useRef<Cue[]>([]);
  const visibleCuesRef = useRef<Cue[]>([]);
  const onSelectFromTable = useCallback(
    (i: number | null) => setSelectedUid(uidAt(visibleCuesRef.current, i)),
    [],
  );
  const onSelectFromStudio = useCallback(
    (i: number | null) => setSelectedUid(uidAt(docCuesRef.current, i)),
    [],
  );
  const onActiveCueChange = useCallback(
    (i: number | null) => setActiveUid(uidAt(docCuesRef.current, i)),
    [],
  );
  const videoFile = studio ? studio.videoFile : localVideo;
  const setVideoFile = studio ? studio.setVideoFile : setLocalVideo;
  const restorableName = studio?.restorableName ?? null;
  const supportsHandles = studio?.supportsHandles ?? false;
  const loadInputRef = useRef<HTMLInputElement>(null);
  // Optional: the Editor also renders in tests without a QcProvider.
  const qc = useContext(QcContext);
  // The active preset's rules, resolved once (a custom preset resolves from the
  // live snapshot; never reads storage) and reused by the 4 cell-colour reads
  // below so the ≤10k-cell render path stays cheap (cue-forge-3r9 §5).
  const { presets: customs } = useCustomPresets();
  const qcPreset = qc?.preset;
  const activeRules = useMemo(
    () => (qcPreset === undefined ? undefined : resolveRuleset(qcPreset, customs).rules),
    [qcPreset, customs],
  );
  // Optional nav context: a pending Inspector "go to cue" jump to consume.
  const nav = useNav();
  const pendingCueJump = nav?.pendingCueJump ?? null;

  // Consume the jump once delivered. The CueTable (a child) scrolls/focuses in
  // its own effect first, then this resets the prop to null so the same cue can
  // be jumped to again later.
  useEffect(() => {
    if (pendingCueJump !== null) nav?.clearCueJump();
  }, [pendingCueJump, nav]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || inTextField(e.target)) return;
      const key = e.key.toLowerCase();
      if (key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if (key === 'y' || (key === 'z' && e.shiftKey)) {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo]);

  if (!loaded) {
    return <EmptyState />;
  }

  const doc = loaded.doc;
  docCuesRef.current = doc.cues;
  // Filter the cue list by the toolbar's Find text (cue-forge-q51); guard against
  // an invalid regex mid-typing by falling back to the full list.
  const filtering = filter.find.trim() !== '';
  let visibleCues = doc.cues;
  if (filtering) {
    try {
      visibleCues = doc.cues.filter((c) => cueMatches(c, filter.find, filter.opts));
    } catch {
      visibleCues = doc.cues;
    }
  }
  visibleCuesRef.current = visibleCues;
  // Resolve the uid-based selection/active pointers to row positions: into the
  // visible list for the table, into the full doc for the studio timeline. Null
  // (no highlight) when the cue is absent from that view (cue-forge-9ww.7/q51).
  const selInVisible = indexOfUid(visibleCues, selectedUid);
  const tableSelectedIndex = selInVisible >= 0 ? selInVisible : null;
  const actInVisible = indexOfUid(visibleCues, activeUid);
  const tableActiveIndex = actInVisible >= 0 ? actInVisible : null;
  const selInDoc = indexOfUid(doc.cues, selectedUid);
  const studioSelectedIndex = selInDoc >= 0 ? selInDoc : null;
  // Match the active QC preset's CPS counting (e.g. netflix-arabic harakat).
  const cpsExcludeRanges = activeRules?.['max-cps']?.excludeRanges;
  // Active-preset thresholds for the cue table's over-limit cell colouring
  // (cue-forge-n3e.4). Only surfaced when the rule is enabled in the preset.
  const cpsRule = activeRules?.['max-cps'];
  const minDurRule = activeRules?.['min-duration'];
  const maxDurRule = activeRules?.['max-duration'];
  const maxCps = cpsRule?.enabled ? cpsRule.max : undefined;
  const minDurationMs = minDurRule?.enabled ? minDurRule.ms : undefined;
  const maxDurationMs = maxDurRule?.enabled ? maxDurRule.ms : undefined;

  const onCommitCue = (updated: Cue) => {
    setDoc({ ...doc, cues: doc.cues.map((c) => (c.index === updated.index ? updated : c)) });
  };

  const onReplaceAll = (find: string, replace: string, opts: FindReplaceOptions): number => {
    const result = findReplace(doc, find, replace, opts);
    if (result.count > 0) setDoc(result.doc);
    return result.count;
  };

  const pickFromInput = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) setVideoFile(f);
    e.target.value = ''; // allow re-picking the same file
  };
  // Open a video via the File System Access picker (re-openable handle) when
  // supported, else the hidden <input> dialog.
  const openVideoPicker = () => {
    if (supportsHandles && studio) void studio.loadViaPicker();
    else loadInputRef.current?.click();
  };
  const onRestore = () => {
    void studio?.restoreVideo().then((r) => {
      if (r === 'repick') openVideoPicker(); // handle gone/unsupported -> re-pick
    });
  };

  return (
    <section className="cf-editor">
      <h1 className="cf-editor__title">
        Studio{' '}
        <span className="cf-editor__count">
          {filtering
            ? `${visibleCues.length} of ${doc.cues.length} cues match`
            : `${doc.cues.length} cues`}
        </span>
        {/* Unsaved-edits chip (cue-forge-drt.19), mirroring Convert's "(edited)"
            badge; markClean (an export) clears it. Without it, dirtiness was only
            discoverable via the beforeunload prompt. */}
        {dirty && <span className="cf-editor__dirty"> (edited)</span>}
      </h1>
      {/* Studio is the default hero and now accepts drops, so import repairs
          (renumber/reversed-timing) and parse warnings must surface here too —
          not only in the Converter (cue-forge-drt.8). Silent repairs / a silent
          0-cue parse are trust-breakers for a compliance tool. */}
      <WarningList
        title="Import"
        warnings={[
          ...loaded.repairs.map((r) => r.message),
          ...loaded.parseWarnings.map((w) => w.message),
        ]}
      />
      {/* Toolbar composed into labeled clusters (cue-forge-pjf): Check (QC),
          Deliver (export), Time (timing), Create (transcription). Small uppercase
          group captions — the Inspector's section-title treatment — map the
          controls so the eye knows what belongs together, and every cluster keeps
          a stable home so nothing jumps between empty/loaded or free/Pro.
          Compliance-QC still leads, above the Pro video upsell (cue-forge-zxw.11),
          since it's the product's core. Each cluster is a role="group"+aria-label
          (a plain group, NOT a landmark region), and the visible caption is
          aria-hidden so AT hears the group name once, not twice. */}
      <div className="cf-studio-toolbar">
        {/* QcControls + the threshold sheet render only with a QC provider
            (always in the app shell; some unit tests mount Editor without it). */}
        {qc && (
          <div className="cf-toolgroup" role="group" aria-label="Check">
            <span className="cf-toolgroup__label" aria-hidden="true">
              Check
            </span>
            <div className="cf-toolgroup__body">
              <QcControls />
              {/* The active preset's threshold sheet (honest verified/draft
                  badge), docked WITH the QC controls rather than floating as a
                  mid-stack row (cue-forge-ve7/pjf). */}
              <QcThresholds />
            </div>
          </div>
        )}
        <div className="cf-toolgroup" role="group" aria-label="Deliver">
          <span className="cf-toolgroup__label" aria-hidden="true">
            Deliver
          </span>
          <div className="cf-toolgroup__body">
            <StudioExport />
          </div>
        </div>
        <div className="cf-toolgroup" role="group" aria-label="Time">
          <span className="cf-toolgroup__label" aria-hidden="true">
            Time
          </span>
          <div className="cf-toolgroup__body">
            {/* Time/Create carry a single bare button; the reserved label line
                (cue-forge-joj) drops it onto the same baseline as the labeled
                Check/Deliver control rows. */}
            <div className="cf-toolgroup__lead">
              <span className="cf-toolgroup__leadspacer" aria-hidden="true" />
              <button
                type="button"
                className="cf-btn"
                aria-expanded={timingOpen}
                // Only reference the panel while it is mounted (cue-forge-zxw.9).
                aria-controls={timingOpen ? 'cf-timing-panel' : undefined}
                onClick={() => setTimingOpen((open) => !open)}
              >
                Timing
              </button>
            </div>
          </div>
        </div>
        <div className="cf-toolgroup" role="group" aria-label="Create">
          <span className="cf-toolgroup__label" aria-hidden="true">
            Create
          </span>
          <div className="cf-toolgroup__body">
            {/* On-device Whisper transcription (cue-forge-sdv.11.4), Pro-gated
                inline like the video subsystem below. ONE stable home here in
                the toolbar (cue-forge-pjf) — the EmptyState keeps its own inline
                affordance for the doc-creating EMPTY-START flow. The reserved
                label line (cue-forge-joj) baselines it with Check/Deliver. */}
            <div className="cf-toolgroup__lead">
              <span className="cf-toolgroup__leadspacer" aria-hidden="true" />
              <TranscribeButton />
            </div>
          </div>
        </div>
      </div>
      {timingOpen && (
        <div id="cf-timing-panel">
          <TimingControls doc={doc} onApply={setDoc} isPro={isPro} />
        </div>
      )}
      <section className="cf-studio-gate" aria-label="Video">
        {!isPro ? (
          // A slim one-liner, not a full pricing card mid-canvas — the free
          // user's cue table and QC are the heroes here (cue-forge-drt.13).
          <UpgradePrompt feature="Video editing" surface="video" compact />
        ) : videoFile ? (
          <div className="cf-studio-loaded">
            <div className="cf-studio-bar">
              <span className="cf-studio-bar__name" title={videoFile.name}>
                {videoFile.name}
              </span>
              {supportsHandles && studio ? (
                <button
                  type="button"
                  className="cf-btn cf-btn--sm"
                  onClick={() => void studio.loadViaPicker()}
                >
                  Replace video
                </button>
              ) : (
                <label className="cf-btn cf-btn--sm">
                  Replace video
                  <input
                    type="file"
                    accept="video/*"
                    className="cf-visually-hidden"
                    aria-label="Replace video"
                    onChange={pickFromInput}
                  />
                </label>
              )}
              <button
                type="button"
                className="cf-btn cf-btn--sm"
                onClick={() => setVideoFile(null)}
              >
                Remove video
              </button>
            </div>
            <Suspense fallback={<p>Loading studio…</p>}>
              <StudioPanel
                file={videoFile}
                doc={doc}
                selectedIndex={studioSelectedIndex}
                onSelectCue={onSelectFromStudio}
                onChangeDoc={setDoc}
                onActiveCueChange={onActiveCueChange}
              />
            </Suspense>
          </div>
        ) : (
          <div className="cf-studio-gate__pick">
            {restorableName && (
              <button type="button" className="cf-btn" onClick={onRestore}>
                Restore video ({restorableName})
              </button>
            )}
            {supportsHandles && studio ? (
              <button
                type="button"
                className="cf-btn cf-studio-gate__load"
                onClick={() => void studio.loadViaPicker()}
              >
                Load video
              </button>
            ) : (
              <label className="cf-btn cf-studio-gate__load">
                Load video
                <input
                  ref={loadInputRef}
                  type="file"
                  accept="video/*"
                  className="cf-visually-hidden"
                  aria-label="Load video"
                  onChange={pickFromInput}
                />
              </label>
            )}
          </div>
        )}
      </section>
      <EditorToolbar
        canUndo={canUndo}
        canRedo={canRedo}
        onUndo={undo}
        onRedo={redo}
        onReplaceAll={onReplaceAll}
        onFilterChange={onFilterChange}
      />
      <CueTable
        cues={visibleCues}
        totalCueCount={doc.cues.length}
        onCommitCue={onCommitCue}
        // Structural edits are position-based and ambiguous over a filtered
        // list (q51), so while filtering merge/split stay visible but disabled
        // with an explanation instead of vanishing (cue-forge-drt.15).
        onMergeNext={(pos) => setDoc(mergeCues(doc, pos))}
        onSplit={(pos) => setDoc(splitCue(doc, pos))}
        // Insert/delete are structural too: disabled while filtering (drt.15).
        // After insert, move the durable selection onto the new cue by its fresh
        // uid (cue-forge-9ww.7) so it's highlighted and ready to edit; after a
        // delete the render-time uid resolution drops the vanished selection.
        onInsertAfter={(pos) => {
          const result = insertCueAfter(doc, pos);
          setDoc(result.doc);
          setSelectedUid(uidAt(result.doc.cues, result.index));
        }}
        onDelete={(pos) => setDoc(deleteCue(doc, pos))}
        mergeSplitDisabled={filtering}
        onAdvancedEdit={(cue) => {
          advLauncherRef.current =
            document.activeElement instanceof HTMLElement ? document.activeElement : null;
          setAdvCue(cue);
          track('advanced_edit_open', {});
        }}
        advancedEditDisabled={!advancedAvailable(doc.metadata.sourceFormat)}
        cpsExcludeRanges={cpsExcludeRanges}
        maxCps={maxCps}
        minDurationMs={minDurationMs}
        maxDurationMs={maxDurationMs}
        frameRate={doc.metadata.frameRate}
        issues={qc?.issues}
        accepted={qc?.accepted}
        scrollToIndex={pendingCueJump}
        selectedIndex={tableSelectedIndex}
        activeIndex={tableActiveIndex}
        onSelectCue={onSelectFromTable}
      />
      {advCue && (
        <Suspense fallback={null}>
          <AdvancedEditDialog
            cue={advCue}
            sourceFormat={doc.metadata.sourceFormat}
            onApply={(lines) => onCommitCue({ ...advCue, lines })}
            onClose={() => {
              setAdvCue(null);
              // Synchronous, before the commit re-renders the row in place — the
              // ShortcutsHelp/QcControls pattern (the dialog never marks the app
              // root inert, so the launcher is focusable here).
              advLauncherRef.current?.focus();
              advLauncherRef.current = null;
            }}
          />
        </Suspense>
      )}
    </section>
  );
}
